import React, { useState, useEffect, useMemo } from 'react';
import { Portfolio, Income, Expense, IncomeForm, ExpenseForm, IncomeType, IncomeFrequency, ExpenseType, ExpenseCategory, FamilyMember } from '../types';
import { formatCurrency } from '../utils/calculations';
import apiService from '../services/api';
import { 
  DollarSign, 
  CreditCard,
  Plus, 
  Edit, 
  Trash2, 
  Filter,
} from 'lucide-react';

interface IncomeExpensesProps {
  portfolio: Portfolio;
  setPortfolio: React.Dispatch<React.SetStateAction<Portfolio>>;
}

type TabType = 'income' | 'expenses';

const incomeTypes: IncomeType[] = ['salary', 'business', 'freelance', 'rental', 'investment', 'pension', 'other'];
const incomeFrequencies: IncomeFrequency[] = ['monthly', 'quarterly', 'yearly'];
const expenseTypes: ExpenseType[] = ['recurring', 'one-off'];
const expenseCategories: ExpenseCategory[] = [
  'Housing', 'Transportation', 'Food & Dining', 'Utilities', 'Healthcare',
  'Insurance', 'Entertainment', 'Education', 'Shopping', 'Travel',
  'Debt Payments', 'Savings & Investments', 'Helpers', 'Other'
];
const expenseFrequencies = ['monthly', 'quarterly', 'yearly'];

const IncomeExpenses: React.FC<IncomeExpensesProps> = ({ portfolio, setPortfolio }) => {
  const [activeTab, setActiveTab] = useState<TabType>('income');
  const [incomes, setIncomes] = useState<Income[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<Income | Expense | null>(null);
  const [incomeFormData, setIncomeFormData] = useState<IncomeForm>({
    familyMemberId: '',
    type: 'salary',
    source: '',
    description: '',
    amount: '',
    frequency: 'monthly',
    startDate: '',
    endDate: '',
    isActive: true,
  });
  const [expenseFormData, setExpenseFormData] = useState<ExpenseForm>({
    familyMemberId: '',
    type: 'recurring',
    category: 'Other',
    name: '',
    description: '',
    amount: '',
    frequency: 'monthly',
    dueDate: '',
    isActive: true,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [incomesData, expensesData] = await Promise.all([
        apiService.getIncomes(),
        apiService.getExpenses(),
      ]);
      setIncomes(incomesData);
      setExpenses(expensesData);
      setPortfolio(prev => ({ ...prev, incomes: incomesData, expenses: expensesData }));
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddIncome = () => {
    setActiveTab('income');
    setEditingItem(null);
    setIncomeFormData({
      familyMemberId: '',
      type: 'salary',
      source: '',
      description: '',
      amount: '',
      frequency: 'monthly',
      startDate: '',
      endDate: '',
      isActive: true,
    });
    setError(null);
    setShowForm(true);
  };

  const handleAddExpense = () => {
    setActiveTab('expenses');
    setEditingItem(null);
    setExpenseFormData({
      familyMemberId: '',
      type: 'recurring',
      category: 'Other',
      name: '',
      description: '',
      amount: '',
      frequency: 'monthly',
      dueDate: '',
      isActive: true,
    });
    setError(null);
    setShowForm(true);
  };

  const handleEditIncome = (income: Income) => {
    setActiveTab('income');
    setEditingItem(income);
    const memberId = typeof income.familyMemberId === 'string' 
      ? income.familyMemberId 
      : (income.familyMemberId as FamilyMember)?._id || '';
    setIncomeFormData({
      familyMemberId: memberId,
      type: income.type,
      source: income.source,
      description: income.description || '',
      amount: income.amount.toString(),
      frequency: income.frequency,
      startDate: income.startDate ? new Date(income.startDate).toISOString().split('T')[0] : '',
      endDate: income.endDate ? new Date(income.endDate).toISOString().split('T')[0] : '',
      isActive: true,
    });
    setError(null);
    setShowForm(true);
  };

  const handleEditExpense = (expense: Expense) => {
    setActiveTab('expenses');
    setEditingItem(expense);
    const memberId = typeof expense.familyMemberId === 'string' 
      ? expense.familyMemberId 
      : (expense.familyMemberId as FamilyMember)?._id || '';
    setExpenseFormData({
      familyMemberId: memberId,
      type: expense.type,
      category: expense.category,
      name: expense.name,
      description: expense.description || '',
      amount: expense.amount.toString(),
      frequency: expense.frequency || 'monthly',
      dueDate: expense.dueDate ? new Date(expense.dueDate).toISOString().split('T')[0] : '',
      isActive: expense.isActive,
    });
    setError(null);
    setShowForm(true);
  };

  const handleDeleteIncome = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this income?')) {
      setError(null);
      try {
        await apiService.deleteIncome(id);
        await fetchData();
      } catch (err) {
        console.error('Error deleting income:', err);
        setError(`Failed to delete income: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
  };

  const handleDeleteExpense = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      setError(null);
      try {
        await apiService.deleteExpense(id);
        await fetchData();
      } catch (err) {
        console.error('Error deleting expense:', err);
        setError(`Failed to delete expense: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
  };

  const handleIncomeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const incomeData = {
      ...incomeFormData,
      amount: parseFloat(incomeFormData.amount),
      familyMemberId: incomeFormData.familyMemberId,
      startDate: incomeFormData.startDate || undefined,
      endDate: incomeFormData.endDate || undefined,
    };

    try {
      if (editingItem && '_id' in editingItem && editingItem._id) {
        await apiService.updateIncome(editingItem._id, incomeData);
      } else {
        await apiService.createIncome(incomeData);
      }
      await fetchData();
      setShowForm(false);
      setEditingItem(null);
    } catch (err) {
      console.error('Error saving income:', err);
      setError(`Failed to save income: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleExpenseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!expenseFormData.familyMemberId || !expenseFormData.name || !expenseFormData.amount) {
      setError('Please fill in all required fields');
      return;
    }

    const expenseData = {
      familyMemberId: expenseFormData.familyMemberId,
      type: expenseFormData.type,
      category: expenseFormData.category,
      name: expenseFormData.name,
      description: expenseFormData.description,
      amount: parseFloat(expenseFormData.amount),
      frequency: expenseFormData.type === 'recurring' ? expenseFormData.frequency : undefined,
      dueDate: expenseFormData.dueDate || undefined,
      isActive: expenseFormData.isActive,
    };

    try {
      if (editingItem && '_id' in editingItem && editingItem._id) {
        await apiService.updateExpense(editingItem._id, expenseData);
      } else {
        await apiService.createExpense(expenseData);
      }
      await fetchData();
      setShowForm(false);
      setEditingItem(null);
    } catch (err) {
      console.error('Error saving expense:', err);
      setError(`Failed to save expense: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const getFamilyMemberName = (memberId: string | FamilyMember): string => {
    if (typeof memberId === 'object') {
      return memberId.name;
    }
    const member = portfolio.familyMembers.find(m => m._id === memberId);
    return member?.name || 'Unknown';
  };

  // Calculate monthly income: convert all active incomes to monthly equivalent
  const monthlyIncome = useMemo(() => {
    return incomes
      .filter(i => i.isActive !== false && i.frequency !== 'one-time')
      .reduce((sum, i) => {
        let monthlyAmount = i.amount;
        if (i.frequency === 'quarterly') {
          monthlyAmount = i.amount / 3;
        } else if (i.frequency === 'yearly') {
          monthlyAmount = i.amount / 12;
        }
        // monthly frequency is already monthly
        return sum + monthlyAmount;
      }, 0);
  }, [incomes]);

  // Calculate yearly income: convert all active incomes to yearly equivalent
  const yearlyIncome = useMemo(() => {
    return incomes
      .filter(i => i.isActive !== false && i.frequency !== 'one-time')
      .reduce((sum, i) => {
        let yearlyAmount = i.amount;
        if (i.frequency === 'monthly') {
          yearlyAmount = i.amount * 12;
        } else if (i.frequency === 'quarterly') {
          yearlyAmount = i.amount * 4;
        }
        // yearly frequency is already yearly
        return sum + yearlyAmount;
      }, 0);
  }, [incomes]);

  const monthlyExpenses = useMemo(() => {
    return expenses
      .filter(e => e.frequency === 'monthly' && e.isActive)
      .reduce((sum, e) => sum + e.amount, 0);
  }, [expenses]);

  const yearlyExpenses = useMemo(() => {
    return expenses
      .filter(e => e.frequency === 'yearly' && e.isActive)
      .reduce((sum, e) => sum + e.amount, 0);
  }, [expenses]);

  const totalMonthlyExpenses = monthlyExpenses + (yearlyExpenses / 12);
  const oneTimeExpenses = useMemo(() => {
    return expenses
      .filter(e => e.type === 'one-off')
      .reduce((sum, e) => sum + e.amount, 0);
  }, [expenses]);

  if (loading) {
    return (
      <div className="p-4 md:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6 w-full max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-3">
            <DollarSign className="h-6 w-6 text-primary-600" />
            <h2 className="text-2xl font-bold text-gray-900">Cash Flow</h2>
          </div>
          <p className="text-gray-600">Manage your income sources and expenses</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('income')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'income'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Income
        </button>
        <button
          onClick={() => setActiveTab('expenses')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'expenses'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Expenses
        </button>
      </div>

      {error && (
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
          <p className="text-danger-700">{error}</p>
        </div>
      )}

      {activeTab === 'income' ? (
        <>
          {/* Income Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <p className="text-sm text-gray-600 mb-2">Monthly Income</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(monthlyIncome)}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <p className="text-sm text-gray-600 mb-2">Yearly Income</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(yearlyIncome)}</p>
            </div>
          </div>

          <button
            onClick={handleAddIncome}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Income
          </button>

          {/* Income List */}
          {incomes.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Income Sources</h3>
              <p className="text-gray-600 mb-6">Add your income sources to track them</p>
              <button
                onClick={handleAddIncome}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add First Income Source
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="divide-y divide-gray-200">
                {incomes.map((income) => (
                  <div key={income._id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{income.source}</h3>
                          <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800">
                            {income.type}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">{getFamilyMemberName(income.familyMemberId)}</p>
                        <p className="text-sm text-gray-500 capitalize">{income.frequency}</p>
                        {income.description && (
                          <p className="text-sm text-gray-600 mt-2">{income.description}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <p className="text-xl font-bold text-gray-900">{formatCurrency(income.amount)}</p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditIncome(income)}
                            className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => income._id && handleDeleteIncome(income._id)}
                            className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {/* Expense Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <p className="text-sm text-gray-600 mb-2">Monthly Expenses</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(monthlyExpenses)}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <p className="text-sm text-gray-600 mb-2">Yearly Expenses</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(yearlyExpenses)}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <p className="text-sm text-gray-600 mb-2">One-time Expenses</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(oneTimeExpenses)}</p>
            </div>
          </div>

          <button
            onClick={handleAddExpense}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Expense
          </button>

          {/* Expense List */}
          {expenses.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Expenses</h3>
              <p className="text-gray-600 mb-6">Add your expenses to track them</p>
              <button
                onClick={handleAddExpense}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add First Expense
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="divide-y divide-gray-200">
                {expenses.map((expense) => (
                  <div key={expense._id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{expense.name}</h3>
                          <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800">
                            {expense.category}
                          </span>
                          <span className={`px-2 py-1 text-xs font-medium rounded ${
                            expense.type === 'recurring' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                          }`}>
                            {expense.type}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">{getFamilyMemberName(expense.familyMemberId)}</p>
                        {expense.frequency && (
                          <p className="text-sm text-gray-500 capitalize">{expense.frequency}</p>
                        )}
                        {expense.description && (
                          <p className="text-sm text-gray-600 mt-2">{expense.description}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <p className="text-xl font-bold text-gray-900">{formatCurrency(expense.amount)}</p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditExpense(expense)}
                            className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => expense._id && handleDeleteExpense(expense._id)}
                            className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Income Form Modal */}
      {showForm && activeTab === 'income' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editingItem ? 'Edit Income' : 'Add New Income'}
            </h3>
            
            {error && (
              <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg">
                <p className="text-sm text-danger-700">{error}</p>
              </div>
            )}

            <form onSubmit={handleIncomeSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="familyMemberId" className="block text-sm font-medium text-gray-700 mb-1">
                  Family Member *
                </label>
                <select
                  id="familyMemberId"
                  name="familyMemberId"
                  value={incomeFormData.familyMemberId}
                  onChange={(e) => setIncomeFormData({ ...incomeFormData, familyMemberId: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select a family member</option>
                  {portfolio.familyMembers.map((member) => (
                    <option key={member._id} value={member._id}>
                      {member.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                  Type *
                </label>
                <select
                  id="type"
                  name="type"
                  value={incomeFormData.type}
                  onChange={(e) => setIncomeFormData({ ...incomeFormData, type: e.target.value as IncomeType })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {incomeTypes.map(type => (
                    <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="source" className="block text-sm font-medium text-gray-700 mb-1">
                  Source *
                </label>
                <input
                  type="text"
                  id="source"
                  name="source"
                  value={incomeFormData.source}
                  onChange={(e) => setIncomeFormData({ ...incomeFormData, source: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Company name, business name, etc."
                />
              </div>

              <div>
                <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
                  Amount *
                </label>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  value={incomeFormData.amount}
                  onChange={(e) => setIncomeFormData({ ...incomeFormData, amount: e.target.value })}
                  required
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label htmlFor="frequency" className="block text-sm font-medium text-gray-700 mb-1">
                  Frequency *
                </label>
                <select
                  id="frequency"
                  name="frequency"
                  value={incomeFormData.frequency}
                  onChange={(e) => setIncomeFormData({ ...incomeFormData, frequency: e.target.value as IncomeFrequency })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {incomeFrequencies.map(freq => (
                    <option key={freq} value={freq}>{freq.charAt(0).toUpperCase() + freq.slice(1)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={incomeFormData.startDate}
                  onChange={(e) => setIncomeFormData({ ...incomeFormData, startDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={incomeFormData.endDate}
                  onChange={(e) => setIncomeFormData({ ...incomeFormData, endDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={incomeFormData.description}
                  onChange={(e) => setIncomeFormData({ ...incomeFormData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="md:col-span-2 flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingItem(null);
                    setError(null);
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  {editingItem ? 'Update' : 'Add'} Income
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Expense Form Modal */}
      {showForm && activeTab === 'expenses' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editingItem ? 'Edit Expense' : 'Add New Expense'}
            </h3>
            
            {error && (
              <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg">
                <p className="text-sm text-danger-700">{error}</p>
              </div>
            )}

            <form onSubmit={handleExpenseSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="expenseFamilyMemberId" className="block text-sm font-medium text-gray-700 mb-1">
                  Family Member *
                </label>
                <select
                  id="expenseFamilyMemberId"
                  name="familyMemberId"
                  value={expenseFormData.familyMemberId}
                  onChange={(e) => setExpenseFormData({ ...expenseFormData, familyMemberId: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select a family member</option>
                  {portfolio.familyMembers.map((member) => (
                    <option key={member._id} value={member._id}>
                      {member.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="expenseType" className="block text-sm font-medium text-gray-700 mb-1">
                  Type *
                </label>
                <select
                  id="expenseType"
                  name="type"
                  value={expenseFormData.type}
                  onChange={(e) => setExpenseFormData({ ...expenseFormData, type: e.target.value as ExpenseType })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {expenseTypes.map(type => (
                    <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <select
                  id="category"
                  name="category"
                  value={expenseFormData.category}
                  onChange={(e) => setExpenseFormData({ ...expenseFormData, category: e.target.value as ExpenseCategory })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {expenseCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="expenseName" className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  id="expenseName"
                  name="name"
                  value={expenseFormData.name}
                  onChange={(e) => setExpenseFormData({ ...expenseFormData, name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label htmlFor="expenseAmount" className="block text-sm font-medium text-gray-700 mb-1">
                  Amount *
                </label>
                <input
                  type="number"
                  id="expenseAmount"
                  name="amount"
                  value={expenseFormData.amount}
                  onChange={(e) => setExpenseFormData({ ...expenseFormData, amount: e.target.value })}
                  required
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {expenseFormData.type === 'recurring' && (
                <div>
                  <label htmlFor="expenseFrequency" className="block text-sm font-medium text-gray-700 mb-1">
                    Frequency *
                  </label>
                  <select
                    id="expenseFrequency"
                    name="frequency"
                    value={expenseFormData.frequency}
                    onChange={(e) => setExpenseFormData({ ...expenseFormData, frequency: e.target.value as any })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {expenseFrequencies.map(freq => (
                      <option key={freq} value={freq}>{freq.charAt(0).toUpperCase() + freq.slice(1)}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 mb-1">
                  Due Date
                </label>
                <input
                  type="date"
                  id="dueDate"
                  name="dueDate"
                  value={expenseFormData.dueDate}
                  onChange={(e) => setExpenseFormData({ ...expenseFormData, dueDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="expenseDescription" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="expenseDescription"
                  name="description"
                  value={expenseFormData.description}
                  onChange={(e) => setExpenseFormData({ ...expenseFormData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="md:col-span-2 flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingItem(null);
                    setError(null);
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  {editingItem ? 'Update' : 'Add'} Expense
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncomeExpenses;

